/**
 * pages/signup/steps/Address.tsx — residential address entry.
 *
 * CIP requires a RESIDENTIAL address (12 CFR §1020.220). PO Box is
 * rejected inline via the shared `isPoBox` helper — same rule as
 * mobile, same rule as the server-side stub.
 *
 * State picker is a plain select for now — a stylised dropdown is a
 * design-agent job. Country is fixed to US for the initial rollout
 * (sponsor bank agreement will constrain this anyway).
 */

import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button, Field } from "../../../components";
import { advance, isPoBox } from "@aster/onboarding";
import { getProgress, setProgress } from "../signup-store";
import { SignupShell } from "../SignupShell";
import styles from "./Address.module.css";

// US state list — abbreviations, alpha sort. Enough for the sponsor-
// bank rollout scope; territories added when needed.
const US_STATES: readonly string[] = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

export function SignupAddress() {
  const navigate = useNavigate();
  const cur = getProgress();

  if (!cur || !cur.date_of_birth) {
    return <Navigate to="/signup/dob" replace />;
  }

  const [line1, setLine1] = useState(cur.address?.line1 ?? "");
  const [line2, setLine2] = useState(cur.address?.line2 ?? "");
  const [city, setCity] = useState(cur.address?.city ?? "");
  const [state, setState] = useState(cur.address?.state ?? "");
  const [postal, setPostal] = useState(cur.address?.postal_code ?? "");
  const [err, setErr] = useState<string | null>(null);
  const [poBoxErr, setPoBoxErr] = useState<string | null>(null);

  // Live PO-box warning on the line1 field. Fires on change so the
  // user sees the rejection before hitting submit.
  const line1PoBox = isPoBox(line1);

  const isValid =
    line1.trim().length >= 2 &&
    !line1PoBox &&
    city.trim().length >= 2 &&
    state.length === 2 &&
    /^\d{5}(-\d{4})?$/.test(postal.trim());

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (line1PoBox) {
      setPoBoxErr(
        "AsterCash requires a residential address. A PO Box can't be used to open an account.",
      );
      return;
    }
    if (!isValid) {
      setErr("Check the address details — every field is required.");
      return;
    }
    setProgress(
      advance(cur, "ssn", {
        address: {
          line1: line1.trim(),
          line2: line2.trim() || undefined,
          city: city.trim(),
          state,
          postal_code: postal.trim(),
          country: "US",
        },
      }),
    );
    navigate("/signup/ssn");
  };

  return (
    <SignupShell
      title="Where do you live?"
      subtitle="Federal rules require a residential address on file — a PO Box or PMB can't be used to open an account."
      step={4}
      backTo="/signup/dob"
      errorBanner={err}
    >
      <form onSubmit={onSubmit} className={styles.form}>
        <Field
          label="Street address"
          value={line1}
          onChange={(v) => {
            setLine1(v);
            if (poBoxErr) setPoBoxErr(null);
          }}
          placeholder="123 Analytical Ave"
          autoComplete="address-line1"
          error={line1PoBox ? "Residential addresses only — no PO Boxes." : poBoxErr ?? undefined}
        />
        <Field
          label="Apartment, suite, unit (optional)"
          value={line2}
          onChange={setLine2}
          placeholder="Apt 4B"
          autoComplete="address-line2"
        />
        <Field
          label="City"
          value={city}
          onChange={setCity}
          autoComplete="address-level2"
        />
        <div className={styles.row}>
          <div className={styles.stateSelect}>
            <label htmlFor="signup-state" className={styles.selectLabel}>
              State
            </label>
            <select
              id="signup-state"
              value={state}
              onChange={(e) => setState(e.currentTarget.value)}
              className={styles.select}
            >
              <option value="">—</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.zipField}>
            <Field
              label="ZIP"
              value={postal}
              onChange={setPostal}
              inputMode="numeric"
              maxLength={10}
              autoComplete="postal-code"
              placeholder="94103"
            />
          </div>
        </div>
        <Button type="submit" disabled={!isValid}>
          Continue
        </Button>
      </form>
    </SignupShell>
  );
}
